import { isEqual } from 'lodash';
import { create } from 'zustand';

import type { EquipmentItem } from '@sb/integrations/types';
import { onGetAllEquipment } from '@sbrc/services';

interface EquipmentStore {
  enabledEquipment: EquipmentItem[];
  allEquipment: EquipmentItem[];
  /** False until the subscription has delivered a snapshot. */
  isLoaded: boolean;
  unsubscribe?: () => void;
}

const useEquipmentStore = create<EquipmentStore>(() => {
  return {
    enabledEquipment: [],
    allEquipment: [],
    isLoaded: false,
  };
});

function subscribeToEquipment() {
  if (useEquipmentStore.getState().unsubscribe) {
    // already subscribed
    return;
  }

  const unsubscribe = onGetAllEquipment((equipment) => {
    const state = useEquipmentStore.getState();

    // The first snapshot has to be written even when it matches the initial
    // empty list, because it is what flips `isLoaded`.
    if (state.isLoaded && isEqual(state.allEquipment, equipment)) {
      return;
    }

    useEquipmentStore.setState({
      allEquipment: equipment,
      enabledEquipment: equipment.filter((device) => device.isEnabled),
      isLoaded: true,
    });
  });

  useEquipmentStore.setState({ unsubscribe });
}

interface UseEquipmentArgs {
  includeDisabled?: boolean;
}

/**
 * The robot's equipment, empty until the subscription delivers its first
 * snapshot. Callers that need to tell that pending state apart from a robot
 * with no equipment want `useValidationEquipment`.
 */
export function useEquipment({
  includeDisabled,
}: UseEquipmentArgs = {}): EquipmentItem[] {
  subscribeToEquipment();

  const equipment = useEquipmentStore();

  if (includeDisabled) {
    return equipment.allEquipment;
  }

  return equipment.enabledEquipment;
}

/**
 * The equipment slice of a `ValidationContext`: `undefined` until the first
 * snapshot arrives, the list itself from then on.
 *
 * Validation reads an empty list as knowledge — this robot supplies no
 * tooltips, so a step naming one is dangling — and reads an absent list as
 * knowing nothing yet. `useEquipment` alone cannot express the difference: it
 * returns `[]` for both, so a context built straight from it accuses every
 * tooltip reference in the routine for as long as the first snapshot takes to
 * arrive, then clears itself.
 *
 * Disabled equipment is included: a step pointing at a switched-off gripper is
 * still pointing at something this robot has, and reporting it as missing
 * would be its own false warning.
 */
export function useValidationEquipment(): EquipmentItem[] | undefined {
  const equipment = useEquipment({ includeDisabled: true });
  const isLoaded = useEquipmentStore((state) => state.isLoaded);

  return isLoaded ? equipment : undefined;
}

/**
 * Whether the equipment subscription has delivered its first snapshot. An empty
 * `useEquipment()` result means "this robot has none" only once this is true;
 * before that, dropping a saved selection that is missing from the list would
 * throw away a device that does exist.
 *
 * The boolean rather than `useValidationEquipment`'s list-or-undefined, for
 * callers that already hold the list and only need to know it is real.
 */
export function useEquipmentHasLoaded(): boolean {
  return useEquipmentStore((state) => state.isLoaded);
}
